import { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Upload, Link as LinkIcon, Image, Eye, EyeOff, Home, ShoppingBag } from 'lucide-react';
import { AdminLayout } from './AdminDashboard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHOP_LINKS = [
  { value: '/products', label: 'All Products' },
  { value: '/products?category=Live+Cats', label: 'Live Cats' },
  { value: '/products?category=Cat+Food', label: 'Cat Food' },
  { value: '/products?category=Accessories', label: 'Accessories' },
  { value: '/products?category=Grooming', label: 'Grooming' },
  { value: '/products?category=Litter+%26+Hygiene', label: 'Litter & Hygiene' },
  { value: '/products?category=Health+%26+Wellness', label: 'Health & Wellness' },
  { value: '/services', label: 'Grooming Services' },
];

const empty = { title: '', caption: '', image_url: '', is_active: true, is_clickable: false, link_url: '/products' };

export default function AdminLandingGallery() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm]         = useState({ ...empty });
  const [saving, setSaving]     = useState(false);
  const [imageMode, setImageMode] = useState('upload');
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const fetchItems = () => {
    setLoading(true);
    axios.get(`${API}/admin/landing-gallery`, { withCredentials: true })
      .then(r => setItems(r.data.items))
      .catch(() => toast.error('Failed to load items'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchItems(); }, []);

  const openNew = () => {
    setEditItem(null);
    setForm({ ...empty });
    setImageMode('upload');
    setDialogOpen(true);
  };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      title: item.title,
      caption: item.caption,
      image_url: item.image_url,
      is_active: item.is_active,
      is_clickable: item.is_clickable || false,
      link_url: item.link_url || '/products',
    });
    setImageMode(item.image_url ? 'url' : 'upload');
    setDialogOpen(true);
  };

  const handleUpload = async (file) => {
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG, WebP, GIF allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/upload/image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setForm(prev => ({ ...prev, image_url: data.image_url }));
      toast.success('Image uploaded to Cloudinary!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop     = (e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.[0]) handleUpload(e.dataTransfer.files[0]); };
  const handleDragOver = (e) => { e.preventDefault(); setDragActive(true); };
  const handleDragLeave = () => setDragActive(false);

  const handleSave = async () => {
    if (!form.image_url) { toast.error('Please upload an image or provide a URL'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await axios.put(`${API}/admin/landing-gallery/${editItem.id}`, form, { withCredentials: true });
        toast.success('Image updated!');
      } else {
        await axios.post(`${API}/admin/landing-gallery`, form, { withCredentials: true });
        toast.success('Image added to landing page!');
      }
      setDialogOpen(false);
      fetchItems();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this image from the landing page?')) return;
    try {
      await axios.delete(`${API}/admin/landing-gallery/${id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchItems();
    } catch { toast.error('Delete failed'); }
  };

  const toggleActive = async (item) => {
    try {
      await axios.put(`${API}/admin/landing-gallery/${item.id}`,
        { ...item, is_active: !item.is_active }, { withCredentials: true });
      fetchItems();
    } catch { toast.error('Failed'); }
  };

  return (
    <AdminLayout active="/admin/landing-gallery">
      <div data-testid="admin-landing-gallery">

        {/* Header */}
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-dark-brown flex items-center gap-2">
              <Home className="w-7 h-7 text-rust" /> Landing Page Gallery
            </h1>
            <p className="font-body text-sm text-light-brown mt-1">
              Upload cat images that appear in the "Meet Our Furry Friends" section on the homepage.
            </p>
          </div>
          <Button
            onClick={openNew}
            className="bg-rust hover:bg-rust/90 text-cream font-body rounded-full"
            data-testid="add-landing-image-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Add Cat Photo
          </Button>
        </div>

        {/* Count badge */}
        <div className="flex gap-3 mb-6">
          <span className="px-4 py-2 bg-rust text-cream rounded-full text-sm font-body font-medium">
            {items.length} Images
          </span>
          <span className="px-4 py-2 bg-white text-mid-brown border border-border-custom rounded-full text-sm font-body">
            {items.filter(i => i.is_active).length} Visible on site
          </span>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-20"><div className="spinner"></div></div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🐱</div>
            <p className="font-body text-light-brown text-lg mb-2">No landing page images yet</p>
            <p className="font-body text-sm text-light-brown/70 mb-6">
              Upload cat photos to display in the homepage gallery section
            </p>
            <Button onClick={openNew} className="bg-rust hover:bg-rust/90 text-cream font-body rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Add First Photo
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map(item => (
              <div
                key={item.id}
                className="group relative bg-white rounded-xl border border-border-custom overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                data-testid={`landing-gallery-item-${item.id}`}
              >
                <div className="relative aspect-square overflow-hidden bg-pale/30">
                  <img
                    src={item.image_url}
                    alt={item.title || 'Cat photo'}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    onError={e => { e.target.src = 'https://images.pexels.com/photos/45201/kitty-cat-kitten-pet-45201.jpeg?w=300&h=300&fit=crop'; }}
                  />

                  {/* Hidden badge */}
                  {!item.is_active && (
                    <span className="absolute top-3 right-3 bg-red-500 text-white text-xs font-body font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                      <EyeOff className="w-3 h-3" /> Hidden
                    </span>
                  )}
                  {item.is_active && (
                    <span className="absolute top-3 left-3 bg-rust text-cream text-xs font-body font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Eye className="w-3 h-3" /> Live
                    </span>
                  )}
                  {item.is_clickable && (
                    <span className="absolute bottom-3 left-3 bg-amber-500 text-white text-xs font-body font-medium px-2 py-0.5 rounded-full flex items-center gap-1">
                      <ShoppingBag className="w-3 h-3" /> Shop Link
                    </span>
                  )}

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-dark-brown/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                    <button
                      onClick={() => openEdit(item)}
                      className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-rust hover:bg-white transition-colors"
                      data-testid={`edit-landing-${item.id}`}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => toggleActive(item)}
                      className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-amber-500 hover:bg-white transition-colors"
                      title={item.is_active ? 'Hide from site' : 'Show on site'}
                    >
                      {item.is_active ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleDelete(item.id)}
                      className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-red-500 hover:bg-white transition-colors"
                      data-testid={`delete-landing-${item.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Caption */}
                <div className="p-3">
                  <h3 className="font-body text-sm font-medium text-dark-brown line-clamp-1">
                    {item.title || 'Cat Photo'}
                  </h3>
                  {item.caption && (
                    <p className="font-body text-xs text-light-brown line-clamp-1 mt-0.5">{item.caption}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add / Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto bg-white">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-dark-brown">
                {editItem ? 'Edit' : 'Add'} Landing Page Cat Photo
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 mt-4">

              <div>
                <Label className="font-body text-sm">Title (optional)</Label>
                <Input
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  className="mt-1 bg-cream/50"
                  placeholder="e.g. Persian Beauty"
                />
              </div>

              <div>
                <Label className="font-body text-sm">Caption (shown on hover)</Label>
                <Input
                  value={form.caption}
                  onChange={e => setForm({ ...form, caption: e.target.value })}
                  className="mt-1 bg-cream/50"
                  placeholder="e.g. Curious Kitten"
                />
              </div>

              {/* Upload toggle */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="font-body text-sm">Cat Image</Label>
                  <div className="flex bg-cream/80 rounded-full p-0.5">
                    <button
                      type="button"
                      onClick={() => setImageMode('upload')}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-body transition-all ${imageMode === 'upload' ? 'bg-rust text-cream shadow-sm' : 'text-mid-brown hover:text-dark-brown'}`}
                    >
                      <Upload className="w-3 h-3" /> Upload from PC
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageMode('url')}
                      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-body transition-all ${imageMode === 'url' ? 'bg-rust text-cream shadow-sm' : 'text-mid-brown hover:text-dark-brown'}`}
                    >
                      <LinkIcon className="w-3 h-3" /> Use URL
                    </button>
                  </div>
                </div>

                {imageMode === 'upload' ? (
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200
                      ${dragActive ? 'border-rust bg-rust/5 scale-[1.01]' : 'border-border-custom hover:border-rust/50 hover:bg-cream/30'}
                      ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      className="hidden"
                      onChange={(e) => handleUpload(e.target.files?.[0])}
                    />
                    {uploading ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="spinner"></div>
                        <p className="font-body text-sm text-mid-brown">Uploading</p>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-12 h-12 rounded-full bg-rust/10 flex items-center justify-center">
                          <Image className="w-6 h-6 text-rust" />
                        </div>
                        <p className="font-body text-sm text-dark-brown font-medium">
                          {dragActive ? 'Drop photo here!' : 'Click or drag cat photo here'}
                        </p>
                        <p className="font-body text-xs text-light-brown">JPG, PNG, WebP, GIF · Max 10MB</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <Input
                    value={form.image_url}
                    onChange={e => setForm({ ...form, image_url: e.target.value })}
                    className="bg-cream/50"
                    placeholder="https://example.com/cat-photo.jpg"
                  />
                )}

                {/* Preview */}
                {form.image_url && (
                  <div className="mt-3 relative group">
                    <img
                      src={form.image_url}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-xl border border-border-custom"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, image_url: '' })}
                      className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center text-red-500 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-50"
                    >
                      ✕
                    </button>
                  </div>
                )}
              </div>

              {/* Clickable toggle */}
              <div className="p-4 bg-cream/60 rounded-xl border border-border-custom space-y-3">
                <div className="flex items-center gap-3">
                  <Switch checked={form.is_clickable} onCheckedChange={v => setForm({ ...form, is_clickable: v })} />
                  <div>
                    <Label className="font-body text-sm font-medium">Make image clickable (Shop Link)</Label>
                    <p className="font-body text-xs text-light-brown mt-0.5">When ON, clicking this photo takes users to your shop</p>
                  </div>
                </div>
                {form.is_clickable && (
                  <div>
                    <Label className="font-body text-xs text-mid-brown mb-1 block">Link to shop section</Label>
                    <Select value={form.link_url} onValueChange={v => setForm({ ...form, link_url: v })}>
                      <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SHOP_LINKS.map(l => <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-2">
                <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
                <Label className="font-body text-sm">Visible on landing page</Label>
              </div>

              <div className="flex gap-3 pt-2">
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-rust hover:bg-rust/90 text-cream font-body rounded-full"
                >
                  {saving ? 'Saving...' : editItem ? 'Update Photo' : 'Add to Landing Page'}
                </Button>
                <Button
                  onClick={() => setDialogOpen(false)}
                  variant="outline"
                  className="font-body rounded-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
